import { classes } from "../react"
import { getLayoutFunction, LayoutFunction } from "./layout"
import { Popup2 } from "./Popup"
import { MaxHeight } from "./types"

interface Props {

    placeholder?: string

    align?: "left" | "right"
    maxHeight?: MaxHeight

    cssClass?: string

    content: React.FunctionComponent<{ text: string, close(): void }>

    text: string
    setText(text: string): void

    onLostFocus?(): void

    openOnFocus?: boolean
    openOnMouseDown?: boolean

    inputStyle?: React.CSSProperties

    readOnly?: boolean
    disabled?: boolean

}

type PopupState =
    | {
        open: false
    }
    | {
        open: true
        layout: LayoutFunction,
    }

export function InputWithPopup(props: Props) {

    const { text, setText, placeholder, align, readOnly, disabled } = props

    const [popupState, setPopupState] = React.useState<PopupState>({ open: false })
    const inputRef = React.useRef<HTMLInputElement>(null)
    const popupRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {

        function handleResize(e: UIEvent) {
            if (popupState.open && inputRef.current) {
                open(inputRef.current)
            }
        }

        document.defaultView!.addEventListener("resize", handleResize)

        return () => document.defaultView!.removeEventListener("resize", handleResize)

    })

    function open(target: HTMLInputElement) {

        const layout = getLayoutFunction(target, { maxHeight: props.maxHeight, align })

        setPopupState({
            open: true,
            layout
        })

    }

    function openOnFocus(target: HTMLInputElement) {

        if (readOnly) {
            return
        }

        if (props.openOnFocus !== false) {
            open(target)
        }

    }

    function openOnMouseDown(target: HTMLInputElement) {

        if (readOnly) {
            return
        }

        if (props.openOnMouseDown !== false) {
            open(target)
        }

    }

    function setTextOnChange(target: HTMLInputElement) {

        const text = target.value

        setText(text)

        if (!popupState.open) {
            open(target)
        }

    }

    function handleBlur() {

        if (props.onLostFocus) {
            props.onLostFocus()
        }

        close()

    }

    function close() {
        setPopupState({ open: false })
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {

        if (readOnly) {
            return
        }

        if ((e.key === "Enter") && (!popupState.open)) {
            e.stopPropagation()
            open(e.target as HTMLInputElement)
        }

    }

    return <>

        <input
            ref={inputRef}
            type="text"
            className={classes("form-control", props.cssClass)}
            style={props.inputStyle}
            placeholder={placeholder}
            value={text}
            readOnly={readOnly}
            disabled={disabled}
            onKeyDown={e => handleKeyDown(e)}
            onMouseDown={e => openOnMouseDown(e.target as HTMLInputElement)}
            onChange={e => setTextOnChange(e.target)}
            onFocus={e => openOnFocus(e.target)}
            onBlur={() => handleBlur()}
        />

        {
            popupState.open &&
            ReactDOM.createPortal(
                <div ref={popupRef}>
                    <Popup2
                        {...{
                            ...popupState,
                            text,
                            close,
                            content: props.content
                        }}
                    />
                </div>, document.body)
        }

    </>

}
